import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';

import { INITIAL_DEMO_BILLS } from '../lib/demoData';

const BillContext = createContext();

export const useBills = () => {
  const context = useContext(BillContext);
  if (!context) {
    throw new Error('useBills must be used within a BillProvider');
  }
  return context;
};

const DEMO_BILLS_KEY = 'spend_easy_demo_bills';

const loadDemoBills = () => {
  try {
    const saved = localStorage.getItem(DEMO_BILLS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.map(b => ({
        ...b,
        dueDate: new Date(b.dueDate),
        createdAt: b.createdAt ? new Date(b.createdAt) : new Date(),
        lastPaidDate: b.lastPaidDate ? new Date(b.lastPaidDate) : null
      }));
    }
  } catch (e) {
    console.error('Error loading demo bills:', e);
  }
  return INITIAL_DEMO_BILLS;
};

const saveDemoBills = (b) => {
  try {
    localStorage.setItem(DEMO_BILLS_KEY, JSON.stringify(b));
  } catch (e) {
    console.error('Error saving demo bills:', e);
  }
};

export const BillProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      setBills([]);
      setLoading(false);
      return;
    }

    if (currentUser.isGuest) {
      const updateGuestData = () => {
        setBills(loadDemoBills());
      };
      updateGuestData();
      setLoading(false);
      window.addEventListener('spend_easy_demo_data_updated', updateGuestData);
      return () => {
        window.removeEventListener('spend_easy_demo_data_updated', updateGuestData);
      };
    }

    const billsRef = collection(db, 'users', currentUser.uid, 'bills');
    const q = query(billsRef, orderBy('dueDate', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const billsList = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        dueDate: docSnap.data().dueDate?.toDate?.() || new Date(),
        createdAt: docSnap.data().createdAt?.toDate?.() || new Date(),
        lastPaidDate: docSnap.data().lastPaidDate?.toDate?.() || null,
      }));
      setBills(billsList);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching bills:', error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [currentUser]);

  const addBill = async (billData) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const newBill = {
        ...billData,
        id: `demo-bill-${Date.now()}`,
        userId: currentUser.uid,
        dueDate: new Date(billData.dueDate),
        isPaid: false,
        createdAt: new Date()
      };
      const updated = [...bills, newBill].sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
      setBills(updated);
      saveDemoBills(updated);
      return { id: newBill.id };
    }

    const billsRef = collection(db, 'users', currentUser.uid, 'bills');
    return addDoc(billsRef, {
      ...billData,
      userId: currentUser.uid,
      dueDate: Timestamp.fromDate(billData.dueDate),
      isPaid: false,
      createdAt: serverTimestamp()
    });
  };

  const updateBill = async (billId, billData) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const updated = bills.map(b => {
        if (b.id === billId) {
          return {
            ...b,
            ...billData,
            dueDate: billData.dueDate ? new Date(billData.dueDate) : b.dueDate,
            updatedAt: new Date()
          };
        }
        return b;
      });
      setBills(updated);
      saveDemoBills(updated);
      return;
    }

    const billRef = doc(db, 'users', currentUser.uid, 'bills', billId);
    const updateData = { ...billData };
    if (billData.dueDate) {
      updateData.dueDate = Timestamp.fromDate(billData.dueDate);
    }
    await updateDoc(billRef, { ...updateData, updatedAt: serverTimestamp() });
  };

  const deleteBill = async (billId) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const updated = bills.filter(b => b.id !== billId);
      setBills(updated);
      saveDemoBills(updated);
      return;
    }

    await deleteDoc(doc(db, 'users', currentUser.uid, 'bills', billId));
  };

  const markBillPaid = async (billId) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const bill = bills.find(b => b.id === billId);
      let nextDueDate = null;
      if (bill?.repeat !== 'none') {
        const currentDue = new Date(bill.dueDate);
        if (bill.repeat === 'monthly') {
          nextDueDate = new Date(currentDue.setMonth(currentDue.getMonth() + 1));
        } else if (bill.repeat === 'yearly') {
          nextDueDate = new Date(currentDue.setFullYear(currentDue.getFullYear() + 1));
        }
      }
      const updated = bills.map(b => {
        if (b.id === billId) {
          return {
            ...b,
            isPaid: nextDueDate ? false : true,
            lastPaidDate: new Date(),
            ...(nextDueDate && { dueDate: nextDueDate })
          };
        }
        return b;
      });
      setBills(updated);
      saveDemoBills(updated);
      return;
    }

    const bill = bills.find(b => b.id === billId);
    const billRef = doc(db, 'users', currentUser.uid, 'bills', billId);
    let nextDueDate = null;
    if (bill?.repeat !== 'none') {
      const currentDue = new Date(bill.dueDate);
      if (bill.repeat === 'monthly') {
        nextDueDate = new Date(currentDue.setMonth(currentDue.getMonth() + 1));
      } else if (bill.repeat === 'yearly') {
        nextDueDate = new Date(currentDue.setFullYear(currentDue.getFullYear() + 1));
      }
    }
    await updateDoc(billRef, {
      isPaid: true,
      lastPaidDate: serverTimestamp(),
      ...(nextDueDate && { dueDate: Timestamp.fromDate(nextDueDate), isPaid: false }),
      updatedAt: serverTimestamp()
    });
  };

  const getBillStatus = (bill) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(bill.dueDate);
    dueDate.setHours(0, 0, 0, 0);
    if (bill.isPaid) return 'paid';
    if (dueDate.getTime() === today.getTime()) return 'due-today';
    if (dueDate < today) return 'overdue';
    return 'upcoming';
  };

  const value = {
    bills,
    loading,
    addBill,
    updateBill,
    deleteBill,
    markBillPaid,
    getBillStatus
  };

  return (
    <BillContext.Provider value={value}>
      {children}
    </BillContext.Provider>
  );
};
